import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import toast from "react-hot-toast";
import { useParams } from "react-router-dom";
import { api } from "../../api/client.ts";
import { keys } from "../../api/queryKeys.ts";
import ListSelector from "../../components/ListSelector.tsx";
import { useTenant } from "../../contexts/TenantContext.tsx";
import BoardStructureCard from "./components/BoardStructureCard.tsx";
import CompanyHeader from "./components/CompanyHeader.tsx";
import CompanyRelationsCard from "./components/CompanyRelationsCard.tsx";
import ContactsCard from "./components/ContactsCard.tsx";
import DetailsCard from "./components/DetailsCard.tsx";
import FinancialsCard from "./components/FinancialsCard.tsx";
import RegistryEventsCard from "./components/RegistryEventsCard.tsx";
import TaxAssessmentsCard from "./components/TaxAssessmentsCard.tsx";
import JobAdsSection from "./components/JobAdsSection.tsx";
import ProspectingCard from "./components/ProspectingCard.tsx";
import NewsMentionsSection from "./components/NewsMentionsSection.tsx";
import PressReleasesSection from "./components/PressReleasesSection.tsx";
import RegulatoryFilingsSection from "./components/RegulatoryFilingsSection.tsx";
import TendersSection from "./components/TendersSection.tsx";
import ScrapedPagesSection from "./components/ScrapedPagesSection.tsx";
import SourceRecordsCard from "./components/SourceRecordsCard.tsx";
import SplitCompanyDialog from "./components/SplitCompanyDialog.tsx";
import TechnologiesCard from "./components/TechnologiesCard.tsx";
import DetailPageStatus from "../../components/detail/DetailPageStatus.tsx";
import SourceDataCard from "../../components/detail/SourceDataCard.tsx";

export default function CompanyDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { selectedTenantId, tenantPath } = useTenant();
  const queryClient = useQueryClient();
  const [listSelectorOpen, setListSelectorOpen] = useState(false);
  const [splitDialogOpen, setSplitDialogOpen] = useState(false);
  const [selectedSourceRecords, setSelectedSourceRecords] = useState<string[]>([]);
  const [selectedJobAds, setSelectedJobAds] = useState<string[]>([]);

  const addToList = useMutation({
    mutationFn: ({ listId }: { listId: string; listName: string }) =>
      api.addProspectsToList(listId, [id!]),
    onSuccess: (_data, { listName }) => {
      queryClient.invalidateQueries({ queryKey: keys.prospectList.all });
      queryClient.invalidateQueries({
        queryKey: keys.masterCompany.prospectMemberships(id),
      });
      toast.success(`Added to "${listName}"`);
    },
  });

  const splitMutation = useMutation({
    mutationFn: () =>
      api.splitCompany(id!, {
        source_record_ids: selectedSourceRecords,
        job_ad_ids: selectedJobAds,
      }),
    onSuccess: (data) => {
      setSplitDialogOpen(false);
      toast.success("Company split");
      queryClient.invalidateQueries({ queryKey: keys.masterCompany.detail(id) });
      queryClient.invalidateQueries({ queryKey: keys.masterCompany.sources(id) });
      queryClient.invalidateQueries({ queryKey: keys.masterCompany.jobAds(id) });
      window.open(tenantPath(`/companies/${data.new_company_id}`), "_blank");
    },
  });

  const company = useQuery({
    queryKey: keys.masterCompany.detail(id),
    queryFn: () => api.getMasterCompany(id!),
    enabled: !!id,
  });

  const sources = useQuery({
    queryKey: keys.masterCompany.sources(id),
    queryFn: () => api.getCompanySources(id!),
    enabled: !!id,
  });

  const technologies = useQuery({
    queryKey: keys.masterCompany.technologies(id),
    queryFn: () => api.getCompanyTechnologies(id!),
    enabled: !!id,
  });

  const financials = useQuery({
    queryKey: keys.masterCompany.financials(id),
    queryFn: () => api.getCompanyFinancials(id!),
    enabled: !!id,
  });

  const taxAssessments = useQuery({
    queryKey: keys.masterCompany.taxAssessments(id),
    queryFn: () => api.getCompanyTaxAssessments(id!),
    enabled: !!id,
  });

  const registryEvents = useQuery({
    queryKey: keys.masterCompany.registryEvents(id),
    queryFn: () => api.getCompanyRegistryEvents(id!),
    enabled: !!id,
  });

  const contacts = useQuery({
    queryKey: keys.masterCompany.contacts(id),
    queryFn: () => api.getCompanyContacts(id!),
    enabled: !!id,
  });

  const companyEdges = useQuery({
    queryKey: keys.masterCompany.edges(id),
    queryFn: () => api.getCompanyEdges(id!),
    enabled: !!id,
  });

  const jobAdsForSplit = useQuery({
    queryKey: keys.masterCompany.jobAds(id),
    queryFn: () => api.getCompanyJobAds(id!),
    enabled: !!id && splitDialogOpen,
  });

  if (company.isLoading || company.isError || !company.data) {
    return (
      <DetailPageStatus
        isLoading={company.isLoading}
        message="Company not found."
        back={{ to: tenantPath("/companies"), label: "Companies" }}
      />
    );
  }

  const c = company.data;

  return (
    <>
      <CompanyHeader
        company={c}
        selectedTenantId={selectedTenantId}
        addToListPending={addToList.isPending}
        onAddToListClick={() => setListSelectorOpen(true)}
        onSplitClick={() => {
          setSelectedSourceRecords([]);
          setSelectedJobAds([]);
          setSplitDialogOpen(true);
        }}
      />
      <DetailsCard company={c} companyId={id!} />
      <ProspectingCard companyId={id!} selectedTenantId={selectedTenantId} />
      {technologies.data && <TechnologiesCard technologies={technologies.data} />}
      {contacts.data && <ContactsCard contacts={contacts.data} />}
      {contacts.data && <BoardStructureCard contacts={contacts.data} companyName={c.name} />}
      {companyEdges.data && (
        <CompanyRelationsCard edges={companyEdges.data} companyName={c.name} companyId={id!} />
      )}
      <FinancialsCard
        companyId={id!}
        financials={financials.data}
        isLoading={financials.isLoading}
      />
      {taxAssessments.data && taxAssessments.data.length > 0 && (
        <TaxAssessmentsCard assessments={taxAssessments.data} />
      )}
      {registryEvents.data && registryEvents.data.length > 0 && (
        <RegistryEventsCard events={registryEvents.data} />
      )}
      <TendersSection companyId={id!} />
      <RegulatoryFilingsSection companyId={id!} />
      <PressReleasesSection companyId={id!} />
      <NewsMentionsSection companyId={id!} />
      <SourceRecordsCard sources={sources.data} />
      <JobAdsSection companyId={id!} />
      <ScrapedPagesSection companyId={id!} />
      <SourceDataCard title="Metadata" data={c.metadata} />
      <ListSelector
        open={listSelectorOpen}
        onClose={() => setListSelectorOpen(false)}
        onSelect={(listId, listName) => {
          setListSelectorOpen(false);
          addToList.mutate({ listId, listName });
        }}
      />
      <SplitCompanyDialog
        open={splitDialogOpen}
        onClose={() => setSplitDialogOpen(false)}
        companyName={c.name}
        sources={sources.data}
        jobAds={jobAdsForSplit.data}
        selectedSourceRecords={selectedSourceRecords}
        onToggleSourceRecord={(id, checked) =>
          setSelectedSourceRecords((prev) =>
            checked ? [...prev, id] : prev.filter((x) => x !== id),
          )
        }
        selectedJobAds={selectedJobAds}
        onToggleJobAd={(id, checked) =>
          setSelectedJobAds((prev) =>
            checked ? [...prev, id] : prev.filter((x) => x !== id),
          )
        }
        onSplit={() => splitMutation.mutate()}
        pending={splitMutation.isPending}
        error={!!splitMutation.error}
      />
    </>
  );
}
